import { useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Sort } from '../../../models/CommonModels';

import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import PageTitle from '../../../components/Text/PageTitle';
import ArrowLeft from '../../../components/Icons/ArrowLeft';
import Table from '../../../components/Table/Table';
import TablePagination from '../../../components/TablePagination/TablePagination';

import TransactionsDetailsFilters from './TransactionsDetailsFilters';
import useTransactionDetails from './useTransactionDetails';

function TransactionsDetails() {
  const theme = useTheme();
  const { id } = useParams();
  const [t] = useTranslation();
  const navigate = useNavigate();

  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<Sort>({ order: 'desc', orderBy: 'creationDate' });

  const { transactionType, isLoading, headers, rows, renderFilterTags, totalItems, exportData } = useTransactionDetails(
    id,
    activePage,
    pageSize,
    sort,
  );

  /**
   * Handle the close action
   */
  const handleClose = () => {
    navigate('/transactions');
  };

  /**
   * On pagination change
   * Perform a new request
   * @param page
   * @param pSize
   */
  const handlePaginationChange = (page: number, pSize: number) => {
    setActivePage(page);
    setPageSize(pSize);
  };

  /**
   * On search
   */
  const handleSearch = () => {
    setActivePage(1);
  };

  /**
   * Handle change of the table sort
   * @param s
   */
  const handleOnSort = (s: Sort) => {
    setSort(s);
  };

  return (
    <Box>
      <Breadcrumbs
        activeLinks={[{ label: t('pages.transactions.title'), url: `/transactions` }]}
        disabledLinks={[transactionType ? t(`pages.transactions.transactionTypes.${transactionType}`) : '']}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', paddingBottom: '2.75rem' }}>
        <ArrowLeft stroke={theme.palette.primary.main} style={{ cursor: 'pointer' }} onClick={handleClose} />
        <PageTitle sx={{ marginLeft: '1rem' }}>
          {transactionType ? t(`pages.transactions.transactionTypes.${transactionType}`) : <Skeleton width="25%" />}
        </PageTitle>
      </Box>
      <Box paddingBottom="3rem">
        <TransactionsDetailsFilters
          onSearch={handleSearch}
          isLoading={isLoading}
          exportData={exportData}
          totalItems={totalItems}
          renderFilterTags={renderFilterTags}
        />
        <Table
          headers={headers}
          rows={rows}
          isLoading={isLoading}
          defaultOrderBy={sort.orderBy}
          defaultOrder={sort.order}
          onSort={handleOnSort}
        />
        <TablePagination
          activePage={activePage}
          pageSize={pageSize}
          totalItems={totalItems}
          handlePageChange={handlePaginationChange}
        />
      </Box>
    </Box>
  );
}

export default TransactionsDetails;
